// Google Pay Express bootstrap for the cart page and the minicart popup.
// Mirrors checkout/applePayExpress: it is invoked from components/miniCart on
// `minicart:show` and on the cart page. It delegates to the Adyen express cart
// framework (aliased to link_adyen), which renders whichever wallets are
// enabled through the Adyen site preferences into #express-container. With only
// Google Pay Express enabled, that is just the Google Pay button.
const deviceUtils = require('../common/utils/device');

// The Adyen Web SDK (window.AdyenWeb) is loaded by a <script> tag that ships
// with the cart/minicart markup (adyen/adyenExpressMetadata). On a full page
// load that script is render-blocking and always ready before this bootstrap
// runs. But when the minicart markup arrives via AJAX (opening the popup,
// updating quantities), jQuery loads that <script> asynchronously, so
// window.AdyenWeb may still be undefined when we reach it. Initialising the
// Adyen express framework before the SDK exists throws
// "window.AdyenWeb is undefined" and the button never renders. Wait for the SDK
// first, mirroring how checkout/applePayExpress waits for its own SDK.
const ADYEN_SDK_WAIT_TIMEOUT_MS = 10000;
const ADYEN_SDK_POLL_INTERVAL_MS = 50;

let awaitingAdyenSdk = false;

function whenAdyenWebReady(callback) {
  if (window.AdyenWeb) {
    callback();
    return;
  }
  // A poll is already in flight; it will run the bootstrap once the SDK is
  // ready, so avoid stacking timers (and duplicate renders) on rapid
  // minicart:show / cart update events.
  if (awaitingAdyenSdk) {
    return;
  }
  awaitingAdyenSdk = true;
  const start = Date.now();
  const timer = setInterval(() => {
    if (window.AdyenWeb) {
      clearInterval(timer);
      awaitingAdyenSdk = false;
      callback();
    } else if (Date.now() - start > ADYEN_SDK_WAIT_TIMEOUT_MS) {
      // SDK never loaded (network failure, blocked CDN). Give up rather than
      // poll forever; a later minicart:show / cart update retries.
      clearInterval(timer);
      awaitingAdyenSdk = false;
    }
  }, ADYEN_SDK_POLL_INTERVAL_MS);
}

function renderGooglePayExpress() {
  // Google Pay Express is only offered on Android. The Adyen SDK would happily
  // mount the button on iOS and on desktop browsers too, so the restriction has
  // to be an explicit user-agent check — the mirror image of
  // checkout/applePayExpress, which bails out on Android.
  if (!deviceUtils.isAndroidUserAgent()) {
    return;
  }
  if (window.areCartExpressPaymentsEnabled !== 'true') {
    return;
  }
  // The Adyen container renderer targets #express-container by id and throws if
  // it is absent, so bail out when it is not on the current view (e.g. empty
  // cart, or Google Pay Express not enabled for this site).
  const expressContainer = document.getElementById('express-container');
  if (!expressContainer) {
    return;
  }
  // The row layout and the reserved button space live in CSS behind
  // .checkout-buttons.has-google-pay-express (see _minicart.scss), keyed on this
  // class instead of on the server-rendered container so that non-Android
  // devices — where the container is still emitted but stays empty — keep the
  // original single-button layout. Marking happens before the idempotence guard
  // below so a container that is already initialised stays marked.
  expressContainer
    .closest('.checkout-buttons')
    ?.classList.add('has-google-pay-express');
  // minicart:show fires on every popup open, including plain re-opens with no
  // cart change (miniCart.js `!updateMiniCart` branch). expressCart.init()
  // wipes and rebuilds #express-container from scratch, so re-running it there
  // remounts the Google Pay button and resets its animation for no reason. A
  // real cart update re-renders the checkoutButtons partial server-side, giving
  // a fresh (unmarked) container node, so marking the current node lets us
  // re-init only when the container is genuinely new.
  if (expressContainer.dataset.googlePayExpressInitialized === 'true') {
    return;
  }
  // The container is already laid out and its height reserved (see the class
  // added above), so this only
  // needs to mount the button into it.
  whenAdyenWebReady(() => {
    expressContainer.dataset.googlePayExpressInitialized = 'true';
    const expressCart = require('app_adyen_SFRA/adyen/express/cart/expressPayments');
    // Idempotent: these guard against binding their body listeners twice.
    expressCart.renderExpressPaymentContainer();
    expressCart.renderGooglePayButton();
    // Fetches the express payment methods and (re)renders into #express-container.
    expressCart.init();
  });
}

module.exports = { renderGooglePayExpress };
